import React, { useState } from "react";
import {
  EmailInput,
  SuggestionBox,
  SuggestionItem,
} from "./EmailInputWithSuggestionsStyles";
import { AnimatePresence, motion } from "framer-motion";
import { shakeAnimation } from "../../styles/animations";
import { Props } from "../../types/componentsConstructors";
import {
  ContentWrapper,
  Icon,
  InputWrapper,
  Label,
  Message,
} from "../ui/FormBaseStyles";

interface EmailInputProps extends Props {
  shakeTrigger?: number;
}

const EmailInputWithSuggestions = ({
  value,
  onChange,
  hasError,
  icon,
  label,
  name = "userEmail",
  placeholder,
  required,
  error,
  shakeTrigger = 0,
}: EmailInputProps) => {
  const [showSuggestions, setShowSuggestions] = useState(false);

  const domains = [
    "@gmail.com",
    "@hotmail.com",
    "@outlook.com",
    "@yahoo.com",
    "@icloud.com",
    "@protonmail.com",
    "@live.com",
    "@bol.com.br",
    "@uol.com.br",
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
    const [, domainPart] = val.split("@");
    setShowSuggestions(val.includes("@") && !domainPart);
  };

  const handleSuggestionClick = (domain: string) => {
    const prefix = value.split("@")[0];
    onChange(prefix + domain);
    setShowSuggestions(false);
  };

  const showError = !!hasError || !!error;

  return (
    <ContentWrapper>
      {label && (
        <Label htmlFor={name}>
          {label}
          {required && <span>*</span>}
        </Label>
      )}

      <motion.div
        key={shakeTrigger}
        animate={showError ? shakeAnimation : { x: 0 }}
        transition={{ duration: 0.3 }}
      >
        <InputWrapper $hasError={showError}>
          {icon && <Icon>{icon}</Icon>}
          <EmailInput
            id={name}
            type="text"
            placeholder={placeholder || "E-mail"}
            value={value}
            onChange={handleInputChange}
            name={name}
            required={required}
            autoComplete="off"
            $hasError={showError}
            aria-invalid={showError}
            aria-describedby={error ? `${name}-error` : undefined}
          />
        </InputWrapper>
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Message id={`${name}-error`}>{error}</Message>
          </motion.div>
        )}
      </AnimatePresence>

      {showSuggestions && (
        <SuggestionBox>
          {domains.map((domain) => (
            <SuggestionItem
              key={domain}
              onClick={() => handleSuggestionClick(domain)}
            >
              {value.split("@")[0]}
              <strong>{domain}</strong>
            </SuggestionItem>
          ))}
        </SuggestionBox>
      )}
    </ContentWrapper>
  );
};

export default EmailInputWithSuggestions;
